import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import {sectionCardSx, headerCellSx, cellSx, kpiCardSx, kpiValueSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, SUCCESS, INFO} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {int, kg} from '../format'
import {NOW} from '../data/reference'
import {plannedImportRows} from './catalogueState'
import {packLabel} from './catalogueMath'
import {chipSx} from '../components/tone'
import DropZone from '../components/DropZone'
import {
  TEMPLATE_NAME, TEMPLATE_COLS, TEMPLATE_ROW, TEMPLATE_NOTES, TEMPLATE_FIELD_MAP,
  MAP_TARGETS, downloadTemplate, sourceColumns,
} from './productTemplate'

const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}
const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const sectionLabelSx = {...labelSx, fontWeight: WEIGHT.semibold, textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5}
const monoCellSx = {...cellSx, fontFamily: 'monospace', fontSize: SIZE['2xs']}

// Month/year for the quick-pick filename below, derived from the pinned app
// clock (NOW = 2026-08-07) rather than the wall clock — see the module-wide
// rule against Date.now()/new Date(): a filename built off the real clock
// would silently drift out of sync with the frozen dataset the rest of this
// page reads.
const NOW_MONTH = NOW.toLocaleDateString('en-US', {month: 'short', timeZone: 'UTC'}).toLowerCase()
const NOW_YEAR = NOW.getUTCFullYear()

/** merchant name -> filename-safe slug, e.g. 'Nobero' -> 'nobero'. */
function slugify(name) {
  return String(name || 'merchant').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'merchant'
}

/** The two quick-pick filenames Step 1 offers in place of an actual file dialog pick. */
export function quickPickNames(merchantName) {
  return [`${slugify(merchantName)}-catalogue-${NOW_MONTH}-${NOW_YEAR}.csv`, 'sku_master_export.xlsx']
}

/** `{column: guess, ...}` seeded from sourceColumns' own guesses — the mapping table's starting point before the merchant touches a single select. */
function initialMapping(sourceCols) {
  const m = {}
  ;(sourceCols || []).forEach((c) => { m[c.column] = c.guess })
  return m
}

/**
 * Whether a template column feeds a Prozo field, resolved to the MAP_TARGETS
 * label plus its TEMPLATE_NOTES annotation. Pure and exported so this
 * derivation (used to render Step 2's "Read as" column when a file matches
 * the Proship template) is unit-tested without mounting the drawer — same
 * shape as MergeSuggestions.candidateStatus / MergeDrawer.mergePreview.
 */
export function templateColumnRead(col) {
  const target = TEMPLATE_FIELD_MAP[col]
  if (!target) return {read: false}
  const label = (MAP_TARGETS.find((t) => t.value === target) || {}).label || target
  return {read: true, label, note: TEMPLATE_NOTES[col] || ''}
}

/**
 * The mapping-table validation the task brief specifies verbatim: exactly
 * one column has to land on SKU code, and no target may be claimed twice.
 * Pure and exported so a change to this rule is tested without mounting the
 * drawer. The caller skips calling this entirely when `usingTemplate` — a
 * file that matched the Proship template is matched by header, so there is
 * nothing here to get wrong.
 */
export function mappingProblems(mapping) {
  const used = {}
  Object.keys(mapping || {}).forEach((k) => {
    if (mapping[k] !== 'skip') used[mapping[k]] = (used[mapping[k]] || 0) + 1
  })
  const problems = []
  if (!used.sku) problems.push('One column has to map to SKU code.')
  Object.keys(used).forEach((t) => {
    if (used[t] > 1) {
      const label = (MAP_TARGETS.find((x) => x.value === t) || {}).label
      problems.push('"' + label + '" is mapped ' + used[t] + ' times.')
    }
  })
  return problems
}

/** Rows added + rows updated — the one number Step 3's KPI tiles, its two tables and the final "Import N rows" button all have to agree on. */
export function plannedImportTotal(rows) {
  return (rows && rows.added ? rows.added.length : 0) + (rows && rows.updated ? rows.updated.length : 0)
}

/** Step 1 — choose a file (or the Proship template, or a quick pick). */
function StepChooseFile({merchantName, onFilePick, onFile, onQuickPick, onWalkTemplate}) {
  // Whether a drag is currently over the zone lives HERE rather than in the
  // drawer: step 1 unmounts the moment a file is chosen, so this resets itself
  // on the way back and never needs a line in the drawer's own closed -> open
  // reset block. A drag that is still in flight when the step changes is not a
  // state worth carrying anywhere.
  const [dragActive, setDragActive] = React.useState(false)

  return (
    <>
      <Alert severity='info' sx={alertSx}>
        A CSV or XLSX export from your own system is fine. Nothing is overwritten until you have seen the summary
        on the last step.
      </Alert>

      {/* THE HONESTY REQUIREMENT for this drawer, matching MergeSuggestions'
          own permanent warning banner: no file this drawer is ever handed is
          actually read. What follows is a preview built from the merchant's
          own catalogue (plannedImportRows), walked through the same three
          steps a real import would use. */}
      <Alert severity='warning' sx={alertSx}>
        No file is read. This sample panel does not parse an upload — picking a file here walks the same three
        steps against a preview built from your own catalogue, so you can see what an import would change.
      </Alert>

      <Box sx={sectionCardSx}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
          Don&apos;t have a file ready?
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, mb: 1.25, lineHeight: 1.55}}>
          {TEMPLATE_COLS.length} columns with one filled example row, matching the live Proship product-master
          importer header for header — including its two long-standing misspellings, which are intentional.
        </Typography>
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
          <Button size='small' sx={secondaryBtnSx} startIcon={<DownloadOutlinedIcon sx={{fontSize: 15}} />} onClick={downloadTemplate}>
            Download {TEMPLATE_NAME}
          </Button>
          <Button size='small' onClick={onWalkTemplate} sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}>
            Walk the flow with it
          </Button>
        </Box>
      </Box>

      <Box>
        <Typography sx={{...labelSx, fontWeight: WEIGHT.semibold, mb: 0.5}}>Choose your own file</Typography>
        <DropZone
          icon={CloudUploadOutlinedIcon}
          big
          label='Drag & drop your file here'
          hint='or click to browse'
          chooseLabel='Browse files'
          accept='.csv,.xlsx,.xls'
          ariaLabel='Choose a catalogue file'
          dragActive={dragActive}
          onPick={onFilePick}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false) }}
          onDrop={(e) => {
            e.preventDefault()
            setDragActive(false)
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0]
            if (dropped) onFile(dropped)
          }}
        />
      </Box>

      <Box>
        <Typography sx={{...labelSx, fontWeight: WEIGHT.semibold, mb: 0.5}}>Or one of these</Typography>
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
          {quickPickNames(merchantName).map((name) => (
            <Button key={name} size='small' sx={secondaryBtnSx} onClick={() => onQuickPick(name)}>
              {name}
            </Button>
          ))}
        </Box>
      </Box>
    </>
  )
}

/** Step 2a — the file matched the Proship template by name, so show what every column would be read as instead of a mapping form. */
function StepTemplateMatch({fileName, total}) {
  return (
    <>
      <Alert severity='success' sx={alertSx}>
        <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{fileName}</Box>{' '}
        matches the Proship template. All {TEMPLATE_COLS.length} headers were recognised across {int(total)} rows,
        so there is nothing to map.
      </Alert>

      <Box sx={{overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: '10px'}}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Template column</TableCell>
              <TableCell sx={headerCellSx}>Example</TableCell>
              <TableCell sx={headerCellSx}>Read as</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TEMPLATE_COLS.map((col, i) => {
              const example = TEMPLATE_ROW[i]
              const isImageUrl = typeof example === 'string' && /^https?:\/\//.test(example)
              const read = templateColumnRead(col)
              return (
                <TableRow key={col}>
                  <TableCell sx={monoCellSx}>{col}</TableCell>
                  <TableCell sx={{...cellSx, fontSize: SIZE['2xs'], color: SUBTLE}}>
                    {isImageUrl ? 'image URL' : (example || '—')}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {read.read ? (
                      <Box>
                        <Chip size='small' label={read.label} sx={chipSx('ok')} />
                        {read.note && (
                          <Typography sx={{fontFamily: FONT, fontSize: '0.65rem', color: SUBTLE, mt: 0.25, lineHeight: 1.4}}>
                            {read.note}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                        Stored, not read by weight freeze
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

/** Step 2b — the file did not match the Proship template, so the merchant maps their own columns against MAP_TARGETS. */
function StepMapColumns({fileName, sourceCols, mapping, onMap, total, problems}) {
  return (
    <>
      <Alert severity='info' sx={alertSx}>
        <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{fileName}</Box> — the {sourceCols.length} columns
        below are typical of a brand&apos;s own export, not read from this file (see the notice on the previous
        step). Map each one against a {int(total)}-row preview drawn from your own catalogue.
      </Alert>

      <Box sx={{overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: '10px'}}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Your column</TableCell>
              <TableCell sx={headerCellSx}>First value</TableCell>
              <TableCell sx={headerCellSx}>Maps to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sourceCols.map((c) => (
              <TableRow key={c.column}>
                <TableCell sx={cellSx}>{c.column}</TableCell>
                <TableCell sx={{...cellSx, color: SUBTLE}}>{c.sample || '—'}</TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    select
                    size='small'
                    fullWidth
                    value={mapping[c.column] || 'skip'}
                    onChange={(e) => onMap(c.column, e.target.value)}
                    SelectProps={{'aria-label': 'Map the column ' + c.column}}
                    sx={{minWidth: 180, fontFamily: FONT, fontSize: SIZE.xs}}
                  >
                    {MAP_TARGETS.map((t) => (
                      <MenuItem key={t.value} value={t.value} sx={{fontFamily: FONT, fontSize: SIZE.xs}}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {problems.length > 0 && (
        <Alert severity='warning' sx={alertSx}>
          {problems.map((p) => <Box key={p}>{p}</Box>)}
        </Alert>
      )}
    </>
  )
}

/** Step 3 — the import summary: two KPI tiles, an Added table, an Updated table, and the closing "this does not touch an approved freeze" notice. */
function StepSummary({rows, packs}) {
  return (
    <>
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: GAP.md}}>
        <Box sx={{...kpiCardSx(SUCCESS), p: 1.5, height: 'auto'}}>
          <Typography sx={{...kpiValueSx(SUCCESS), fontSize: SIZE.xl}}>{int(rows.added.length)}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
            Rows added, no matching code in the catalogue
          </Typography>
        </Box>
        <Box sx={{...kpiCardSx(INFO), p: 1.5, height: 'auto'}}>
          <Typography sx={{...kpiValueSx(INFO), fontSize: SIZE.xl}}>{int(rows.updated.length)}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
            Rows updated against an existing code
          </Typography>
        </Box>
      </Box>

      {rows.added.length > 0 && (
        <Box>
          <Typography sx={sectionLabelSx}>Added</Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>SKU code</TableCell>
                <TableCell sx={headerCellSx}>Sub-category</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead weight</TableCell>
                <TableCell sx={headerCellSx}>Packing material</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.added.map((r) => (
                <TableRow key={r.sku}>
                  <TableCell sx={monoCellSx}>{r.sku}</TableCell>
                  <TableCell sx={cellSx}>{r.sub}</TableCell>
                  <TableCell sx={{...cellSx, textAlign: 'right'}}>{kg(r.dead)}</TableCell>
                  <TableCell sx={cellSx}>{packLabel(packs, r.packId)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {rows.updated.length > 0 && (
        <Box>
          <Typography sx={sectionLabelSx}>Updated</Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>SKU code</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead weight</TableCell>
                <TableCell sx={headerCellSx}>Packing material</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.updated.map((r) => (
                <TableRow key={r.sku}>
                  <TableCell sx={monoCellSx}>{r.sku}</TableCell>
                  <TableCell sx={{...cellSx, textAlign: 'right'}}>
                    <Box component='span' sx={{color: SUBTLE}}>{kg(r.fromDead)}</Box> {'→'} {kg(r.dead)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Box component='span' sx={{color: SUBTLE}}>{packLabel(packs, r.fromPack)}</Box> {'→'} {packLabel(packs, r.packId)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Alert severity='warning' sx={alertSx}>
        Imported dead weights do not change an approved weight freeze on their own. A frozen SKU keeps its agreed
        weight until a fresh request is approved.
      </Alert>
    </>
  )
}

/**
 * The catalogue upload wizard — Catalogue Task 6. Three steps driven by
 * local `step` (1|2|3) and a `usingTemplate` flag: Step 1 picks a file (or
 * the Proship template, or a quick pick); Step 2 is either "here is what
 * was recognised" (a name matching the Proship template) or a column-mapping
 * form; Step 3 is the import summary.
 *
 * NOTHING IS PARSED — see the permanent warning Alert in Step 1, and
 * catalogueState.js's own `plannedImportRows`, which this drawer calls for
 * every number and row it shows. `skus`/`packs` are the live store arrays
 * threaded down from CataloguePage, so the preview always reflects whatever
 * merges/reviews have already happened in this session, exactly like
 * MergeDrawer's own preview does for a merge.
 *
 * Local `step`/`usingTemplate`/`fileName`/`mapping` are genuinely ephemeral
 * drawer UI — none of it is read by any other section, so none of it
 * belongs in the shared store (see the module-wide rule against a second
 * store). The one durable action this drawer can take — actually importing
 * the rows — is handed back to the caller via `onImport(rows, fileName)`,
 * which CataloguePage wires to the store's own `importCatalogue` action.
 *
 * State resets on the closed -> open transition, not on every render — the
 * same "adjusted during render" pattern MergeDrawer uses to reset its own
 * mother pick, rather than a stale step/mapping carrying over from the last
 * time this same component instance was shown.
 *
 * Keyboard-dismissable and focus-return are handled by MUI's underlying
 * Modal (Escape key, backdrop click, restore focus to the trigger) exactly
 * like MergeDrawer; CataloguePage's own onClose additionally falls back to
 * focusing the active rail item if that restore ever lands on `<body>` (see
 * its own comment) — the same fallback the merge drawer already uses,
 * reused rather than re-invented for this drawer.
 */
export default function UploadDrawer({open, skus, packs, merchantName, onClose, onImport}) {
  const sourceCols = React.useMemo(() => sourceColumns(skus, packs), [skus, packs])
  const rows = React.useMemo(() => plannedImportRows(skus, packs), [skus, packs])
  const total = plannedImportTotal(rows)

  const [step, setStep] = React.useState(1)
  const [usingTemplate, setUsingTemplate] = React.useState(false)
  const [fileName, setFileName] = React.useState('')
  const [mapping, setMapping] = React.useState(() => initialMapping(sourceCols))

  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setStep(1)
      setUsingTemplate(false)
      setFileName('')
      setMapping(initialMapping(sourceCols))
    }
  }

  const problems = usingTemplate ? [] : mappingProblems(mapping)

  // One funnel, two entry points. The picker hands back a change event and the
  // drop zone hands back the File it pulled off `dataTransfer`, so the shape
  // difference is absorbed here rather than making the zone fake an event.
  const takeFile = (f) => {
    if (!f) return
    setFileName(f.name)
    setUsingTemplate(/product_master/i.test(f.name))
    setStep(2)
  }

  const handleFilePick = (e) => {
    const f = e.target.files && e.target.files[0]
    // Cleared so Back -> step 1 -> the SAME file still fires a change event.
    // Without it the second pick is a no-op and the wizard silently stalls.
    e.target.value = ''
    takeFile(f)
  }

  const handleQuickPick = (name) => {
    setFileName(name)
    setUsingTemplate(false)
    setStep(2)
  }

  const handleWalkTemplate = () => {
    setFileName(TEMPLATE_NAME)
    setUsingTemplate(true)
    setStep(2)
  }

  const handleMap = (column, value) => {
    setMapping((m) => ({...m, [column]: value}))
  }

  const handleImport = () => {
    onImport(rows, fileName || TEMPLATE_NAME)
  }

  const subtitle = `Step ${step} of 3 · ${step === 1 ? 'choose a file' : step === 2 ? (usingTemplate ? 'confirm what was recognised' : 'map the columns') : 'import summary'}`

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 720}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Upload catalogue
              </Typography>
              <Typography sx={labelSx}>{subtitle}</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close upload drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          {step === 1 && (
            <StepChooseFile
              merchantName={merchantName}
              onFilePick={handleFilePick}
              onFile={takeFile}
              onQuickPick={handleQuickPick}
              onWalkTemplate={handleWalkTemplate}
            />
          )}
          {step === 2 && usingTemplate && <StepTemplateMatch fileName={fileName} total={total} />}
          {step === 2 && !usingTemplate && (
            <StepMapColumns
              fileName={fileName}
              sourceCols={sourceCols}
              mapping={mapping}
              onMap={handleMap}
              total={total}
              problems={problems}
            />
          )}
          {step === 3 && <StepSummary rows={rows} packs={packs} />}
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          {step === 1 && <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>}
          {step === 2 && (
            <>
              <Button sx={secondaryBtnSx} onClick={() => setStep(1)}>Back</Button>
              <Button sx={primaryBtnSx} disabled={!usingTemplate && problems.length > 0} onClick={() => setStep(3)}>
                Preview import
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button sx={secondaryBtnSx} onClick={() => setStep(2)}>
                {usingTemplate ? 'Back to the columns' : 'Back to mapping'}
              </Button>
              <Button sx={primaryBtnSx} onClick={handleImport}>
                Import {int(total)} rows
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
